/*
 * Copyright (c) 2018 teamapps.org (see code comments for author's name)
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import * as log from "loglevel";
import {UiToolButtonConfig} from "../../generated/UiToolButtonConfig";
import {SpecialToolButtonType, UiPanel} from "../UiPanel";
import {TeamAppsEvent} from "../util/TeamAppsEvent";
import {UiComponentConfig} from "../../generated/UiComponentConfig";
import {TeamAppsUiContext} from "../TeamAppsUiContext";
import {UiTabPanel} from "../UiTabPanel";
import * as $ from "jquery";
import {UiTabPanelTabStyle} from "../../generated/UiTabPanelTabStyle";
import {bind} from "../util/Bind";
import {ItemTreeItem} from "./ItemTree";
import {UiComponent} from "../UiComponent";
import {View} from "./View";
import {SplitPaneItem} from "./SplitPaneItem";
import {generateUUID} from "../Common";

export enum TabPanelItemState {
	NORMAL, MAXIMIZED, MINIMIZED
}

class MinimizableTabPanel extends UiTabPanel {

	private _minimized = false;

	set minimized(minimized: boolean) {
		this._minimized = minimized;
		this.onEmptyStateChanged.fireIfChanged(this.empty);
	}

	get empty() {
		return this.getNumberOfVisibleTabButtons() === 0 || this._minimized;
	}
}

export class TabPanelItem implements ItemTreeItem<UiTabPanel> {
	private static logger = log.getLogger("UiWorkSpaceLayout.TabPanelItem");

	parent: SplitPaneItem | null;
	component: MinimizableTabPanel;
	tabs: View[] = [];
	$minimizedButton: JQuery = $(`<div class="minimized-tabpanel-button"></div>`);

	public readonly onTabSelected: TeamAppsEvent<{ tabId: string }> = new TeamAppsEvent(this);
	public readonly onTabNeedsRefresh: TeamAppsEvent<{ tabId: string }> = new TeamAppsEvent(this);
	public readonly onTabClosed: TeamAppsEvent<string> = new TeamAppsEvent(this);
	public readonly onMinimizeButtonPressed: TeamAppsEvent<TabPanelItem> = new TeamAppsEvent<TabPanelItem>(this);
	public readonly onMaximizeButtonPressed: TeamAppsEvent<TabPanelItem> = new TeamAppsEvent<TabPanelItem>(this);
	public readonly onRestoreButtonPressed: TeamAppsEvent<TabPanelItem> = new TeamAppsEvent<TabPanelItem>(this);

	private _state: TabPanelItemState = TabPanelItemState.NORMAL;

	constructor(parent: SplitPaneItem, private context: TeamAppsUiContext) {
		this.parent = parent;
		let uuid = generateUUID();
		this.component = new MinimizableTabPanel({
			_type: "UiTabPanel",
			id: uuid,
			hideTabBarIfSingleTab: true,
			tabStyle: UiTabPanelTabStyle.EARS,
			toolButtons: this.createSpecialToolButtonConfigs(this.maximized, false)
		}, context);
		this.component.onToolButtonClicked.addListener(eventObject => {
			let buttonId = eventObject.toolButtonId;
			if (buttonId === 'minimize') {
				this.onMinimizeButtonPressed.fire(this);
			} else if (buttonId === 'maximize') {
				this.onMaximizeButtonPressed.fire(this);
			} else if (buttonId === 'restore') {
				this.onRestoreButtonPressed.fire(this);
			} else {
				TabPanelItem.logger.warn(`Unknown tool button pressed: ${buttonId}`);
			}
		});
		this.component.onTabSelected.addListener(eventObject => this.onTabSelected.fire(eventObject));
		this.component.onTabNeedsRefresh.addListener(eventObject => this.onTabNeedsRefresh.fire(eventObject));
		this.component.onTabClosed.addListener(eventObject => {
			this.tabs = this.tabs.filter(tab => tab.viewName !== eventObject.tabId);
			this.onTabClosed.fire(eventObject.tabId);
			this.updateSpecialToolButtons();
		});
	}

	private createSpecialToolButtonConfigs(restore: boolean, close: boolean) {
		let iconSize = this.context.config.optimizedForTouch ? 16 : 12;
		let buttonTypes: UiToolButtonConfig[] = [UiPanel.createSpecialToolButtonConfig(SpecialToolButtonType.MINIMIZE, iconSize)];
		if (restore) {
			buttonTypes.push(UiPanel.createSpecialToolButtonConfig(SpecialToolButtonType.RESTORE, iconSize));
		} else {
			buttonTypes.push(UiPanel.createSpecialToolButtonConfig(SpecialToolButtonType.MAXIMIZE, iconSize));
		}
		if (close) {
			buttonTypes.push(UiPanel.createSpecialToolButtonConfig(SpecialToolButtonType.CLOSE, iconSize));
		}
		return buttonTypes;
	}

	public addTab(view: View, select: boolean): void {
		this.component.addTabInstance(view.component, {
			tabId: view.viewName,
			icon: view.tabIcon,
			caption: view.tabCaption,
			closeable: view.tabCloseable,
			rightSide: false,
			lazyLoading: view.lazyLoading
		}, null, select, false);

		this.tabs.push(view);

		view.onSpecialToolButtonPressed.addListener(this.specialViewToolButtonPressed);

		this.updateSpecialToolButtons();

		view.parent = this;

		if (this.state === TabPanelItemState.MINIMIZED) {
			this.updateMinimizedButton();
		}
	}

	@bind
	private specialViewToolButtonPressed(buttonId: string, view: View) {
		if (buttonId === 'minimize') {
			this.onMinimizeButtonPressed.fire(this);
		} else if (buttonId === 'maximize') {
			this.onMaximizeButtonPressed.fire(this);
		} else if (buttonId === 'restore') {
			this.onRestoreButtonPressed.fire(this);
		} else if (buttonId === 'close') {
			this.removeTab(view, true);
			this.onTabClosed.fire(view.viewName);
		} else {
			TabPanelItem.logger.warn(`Unknown tool button pressed: ${buttonId}`);
		}
	}

	public removeTab(view: View, destroyContentComponent: boolean) {
		view.onSpecialToolButtonPressed.removeListener(this.specialViewToolButtonPressed);
		this.component.removeTab(view.viewName, destroyContentComponent);
		this.tabs = this.tabs.filter(tab => tab !== view);
		this.updateSpecialToolButtons();

		if (this.state == TabPanelItemState.MINIMIZED) {
			this.updateMinimizedButton();
		}
	}

	public updateTab(viewName: string, component: UiComponent<UiComponentConfig>) {
		this.component.setTabContentInstance(viewName, component);
		this.updateSpecialToolButtons();
	}

	setTabConfiguration(viewName: string, tabIcon: string, tabCaption: string, tabCloseable: boolean) {
		this.component.setTabConfiguration(viewName, tabIcon, tabCaption, tabCloseable, false);
		this.updateSpecialToolButtons();
	}

	selectTab(viewName: string) {
		this.component.selectTab(viewName, false);
	}

	private updateSpecialToolButtons() {
		if (this.component.getNumberOfVisibleTabButtons() > 1) {
			this.tabs.forEach(view => {
				view.setSpecialToolButtons(null);
			});
			this.component.setToolButtons(this.createSpecialToolButtonConfigs(this.maximized, false));
		} else {
			this.tabs.forEach(view => {
				view.setSpecialToolButtons(this.createSpecialToolButtonConfigs(this.maximized, view.tabCloseable));
			});
		}
	}

	get maximized() {
		return this._state === TabPanelItemState.MAXIMIZED
	}

	set state(state: TabPanelItemState) {
		this._state = state;
		let minimized = state === TabPanelItemState.MINIMIZED;
		this.component.minimized = minimized;
		if (minimized) {
			this.updateMinimizedButton();
		}
		this.updateSpecialToolButtons();
	}

	private updateMinimizedButton() {
		let iconSize = this.context.config.optimizedForTouch ? 16 : 12;
		this.$minimizedButton.empty();
		// noinspection CssUnknownTarget
		this.$minimizedButton.append(`<div class="tab-icon img img-${iconSize}" style="background-image: url('/static/resources/window-restore-grey.png')"></div>`);
		this.tabs.forEach(tab => this.$minimizedButton.append(`<div class="tab-icon img img-${iconSize}" style="background-image: url('${this.context.getIconPath(tab.tabIcon, iconSize)}')"></div>`));
		this.$minimizedButton.click(() => this.onRestoreButtonPressed.fire(this));
	}

	get state(): TabPanelItemState {
		return this._state;
	}

}


